import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styles from './QuizBuilder.module.scss';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  useGetAllUnitsWithQuizLazyQuery,
  useGetMyQuizzesLazyQuery,
  useDeleteQuizMutation,
  Quiz,
  Unit,
  useCreateQuizMutation,
  useUpdateQuizMutation,
  UnitsByProgram,
  UnitContentFragment,
  useUpdateQuizNameMutation,
  FeedbackFormContent
} from '@api/graphql-codegen';
import Spinner from 'src/components/spinner/Spinner';
import Header from 'src/components/header/Header';
import AddQuiz from './quiz-modal/AddQuiz';
import GridUnitsLayout from './gridLayout/GridUnitsLayout';
import DeleteModal from '@components/modal/delete-modal/DeleteModal';
import { Collapse } from '@src/utils/componentCasts';
import './customStyle.css';
import {
  useUser,
  useQueryParams,
  CourseContentProvider,
  useCourseContent
} from '@hooks';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import { MyQuizEnum, ExtendedQuiz } from '@types';
import TabLayout from './tabLayout/TabLayout';
import Button from '@src/components/button/Button';
import EditQuiz from './quiz-modal/EditQuiz';
import { useMediaQuery } from 'react-responsive';
import { TouchBackend } from 'react-dnd-touch-backend';
import {
  DndProvider,
  TouchTransition,
  MouseTransition,
  usePreview
} from 'react-dnd-multi-backend'
import { getMediaAbsolutePath } from '@services/File';
import { useHistory } from 'react-router-dom';
import { auProgramGradeLevels, createURL, usProgramGradeLevels } from '@src/constants';
import IconMark from 'src/assets/icons/mark_completed.svg?react';
import UnitProgressModal from '@src/components/unit-progress-modal/UnitProgressModal';
import InstructionLessonRun from '@src/components/lesson-details/instruction-modal/InstructionLessonRun';
import GenerateExitTicketLinkModal from '@src/components/program-details/exitticket-modal/GenerateExitTicketLinkModal';
import QuizPreviewModal from './quiz-preview/QuizPreviewModal';
import QuizItem from './quiz-item/QuizItem';
import { useToast } from '@src/hooks/useToast';
import { logger } from '../../utils/logger';

export const colors = ['#FFB3B3', '#CADEFC', '#9EE6CF', '#F0D9FF', '#DFF4F3', '#FFCFDF', '#FFDBA4', '#FFD9C0', '#DEECFF', '#CDF0EA', '#FFE9AE', '#C1EFFF', '#D9D6FF', '#FFF3B3'];
export const darkColors = ['#FF7E7E', '#94BFFF', '#4ECCA4', '#BB7AE4', '#69D7D1', '#FC75A2', '#FFB648', '#FFA467', '#A4A8FF', '#69D7C4', '#FAC947', '#51D2FF', '#8075FC', '#FFD600'];

function checkRepeat(array: ExtendedQuiz[], el: string) {
  for(var i = 0 ; i < array.length; i++)
    if(array[i].color === el) return true;
  return false;
}

function getRandColor(quizzes: ExtendedQuiz[], array: string[]) {
  var rand = array[Math.floor(Math.random()*array.length)];
  if(!checkRepeat(quizzes, rand)) {
     return rand;
  }
  return getRandColor(quizzes, array);
}

const emptyQuiz: ExtendedQuiz = {
  id: '',
  name: '',
};

function QuizBuilderComponent() {
  const history = useHistory();
  const params = useQueryParams();
  const tab = params.get('tab');
  const unitId = params.get('uid');
  const [showModal, setShowModal] = useState(false);
  const [showUnit, setShowUnit] = useState(false);
  const [selectUnit, setSelectUnit] = useState<Unit | null>(null);
  // const [myQuizzes, setMyQuizzes] = useState<ExtendedQuiz[]>([]);
  const [showMyQuiz, setShowMyQuiz] = useState(true);
  const [name, setName] = useState('')
  const [color, setColor] = useState<string | null>(null)
  const [myQuizMode, setMyQuizMode] = useState(tab === '2' ? MyQuizEnum.MyQuizzes : MyQuizEnum.QuizBuilder)
  const [selMyQuiz, setSelMyQuiz] = useState<ExtendedQuiz | undefined>(undefined);
  const [removeConfirmation, setRemoveConfirmation] = useState(false);
  const [deletingQuizId, setDeletingQuizId] = useState<string>();
  const [showEditModal, setShowEditModal] = useState(false);
  const [dndLesson, setDndUnit] = useState<string>();
  const HEADER_HEIGHT = 88;
  const [topPos, setTopPos] = useState(HEADER_HEIGHT);
  const [navHidden, setNavHidden] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [user] = useUser();
  const [isMovable, setIsMovable] = useState(false);

  const [hoveredLessonId, setHoveredLessonId] = useState("");
  const [needBottomPadding, setNeedBottomPadding] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [centerCard, setCenterCard] = useState(false);

  const [showQuizSessionModal, setShowQuizSessionModal] = useState(false);
  const [showQuizGeneration, setShowQuizGeneration] = useState(false);
  const [selGenQuiz, setSelGenQuiz] = useState<ExtendedQuiz | undefined>(undefined);
  const [showQuizPreview, setShowQuizPreview] = useState(false);
  const [showQuizView, setShowQuizView] = useState(false);
  const [movedScroll, setMovedScroll] = useState(false);
  let countDownTimer = useRef<NodeJS.Timeout | null>(null);

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
  }, []);

  const { successToast } = useToast();
  const { myQuizzes, setMyQuizzes } = useCourseContent();

  const isMobile = useMediaQuery({maxWidth: 800});
  const middleWidth = useMediaQuery({maxWidth: 1330});
  const smallWidth = useMediaQuery({maxWidth: 1024});
  const isAUuser = useMemo(() => user?.id?.toLowerCase().startsWith('au'), [user?.id]);

  const [getMyQuizzes, {data: myQuizData, loading: quizLoading, refetch}] = useGetMyQuizzesLazyQuery({fetchPolicy: 'network-only'});
  const [getAllUnitsWithQuiz, {data: UnitData, loading}] = useGetAllUnitsWithQuizLazyQuery({fetchPolicy: 'network-only'});
  const [deleteQuiz, { loading: deletingCourse }] = useDeleteQuizMutation({
    onCompleted: () => {
      if (deletingQuizId === selMyQuiz?.id) {
        setSelMyQuiz(undefined)
      }
    }
  });

  useEffect(() => {
    history.replace(`/assessments?tab=${myQuizMode === MyQuizEnum.MyQuizzes ? '2' : '1'}` + (unitId ? `&uid=${unitId}` : ''))
  }, [myQuizMode, history, unitId])

  const filtered = useMemo(() => {
    let programs: UnitsByProgram[] = UnitData?.getAllUnitsWithQuiz || [];
    let allUnits: Unit[] = [] 
    programs.forEach(program => {
      if (program.units) {
        allUnits = [...allUnits, ...program.units]
      }
    })
    return allUnits;
  }, [UnitData?.getAllUnitsWithQuiz]);

  useEffect(() => {
    if (!unitId || unitId === selectUnit?.id) return;
    const unit = filtered.find(unit => unit.id && unit.id === unitId) as Unit
    if (unit?.id) {
      setSelectUnit(unit);
      history.replace(`/assessments?tab=${myQuizMode === MyQuizEnum.MyQuizzes ? '2' : '1'}&uid=${unit?.id}`);
      setShowUnit(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, selectUnit?.id, filtered])

  useEffect(() => {
    if (tab === '2') {
      setMyQuizMode(MyQuizEnum.MyQuizzes)
    } else {
      setMyQuizMode(MyQuizEnum.QuizBuilder)
    }
  }, [tab])

  useEffect(() => {
    getMyQuizzes();
  }, [getMyQuizzes])

  useEffect(() => {
    getAllUnitsWithQuiz();
  }, [getAllUnitsWithQuiz])

  const [createQuiz, {loading: creating}] = useCreateQuizMutation({
    onCompleted(data) {
      if (showModal) setShowModal(false);
      if (showEditModal) setShowEditModal(false);
      if (data.createQuiz) {
        const newColor = color ? color : getRandColor(myQuizzes, colors)
        const foundIdx = colors.findIndex(el => el === newColor)
        const darkColor = darkColors[foundIdx]
        
        const plan = {
          id: data.createQuiz,
          name: name,
          color: newColor,
          darkColor,
          unitIds: dndLesson ? [dndLesson] : (selectUnit ? [selectUnit.id] : []),
          feedbackForm: undefined,
          expiryMinutes: 0,
          status: false
        }
        setSelMyQuiz(plan);
        if(dndLesson) setDndUnit(undefined);
        const addedQuizzes = [...myQuizzes, plan];
        setMyQuizzes(addedQuizzes)
        
        if (selectUnit) {
          updateQuiz({
            variables: {
              input: {
                id: data.createQuiz,
                unitIds: [selectUnit.id]
              }
            }
          });
        }
      }
    },
    onError(e) {
      if (showModal) setShowModal(false);
      if (showEditModal) setShowEditModal(false);
      logger.info('error', e.message)
    }
  })

  const [updateQuiz, {loading: updating}] = useUpdateQuizMutation({
    onCompleted(data) {
      if (data.updateQuiz) {
        successToast("Assessment updated successfully!");
      }
    },
    update(_, __, { variables }) {
      if (variables?.input.id) {
        const index = myQuizzes.findIndex(plan => plan.id === variables?.input.id);
        if (index > -1) {
          const quiz = {
            id: variables?.input.id,
            name: myQuizzes[index].name,
            color: myQuizzes[index].color,
            darkColor: myQuizzes[index].darkColor,
            unitIds: (variables?.input.unitIds || []) as string[],
            feedbackForm: myQuizzes[index].feedbackForm,
            expiryMinutes: myQuizzes[index].expiryMinutes,
            status: myQuizzes[index].status,
          };
          const newQuizzes = [...myQuizzes];
          newQuizzes[index].unitIds = (variables?.input.unitIds || []) as string[];
          setSelMyQuiz(quiz);
        }
      }
    },
    onError(e) {
      setShowModal(false);
      logger.info('error', e.message)
    }
  })

  const [updateQuizName, {loading: updating1}] = useUpdateQuizNameMutation({
    onCompleted(data) {
      if (showEditModal) setShowEditModal(false);
      if (data.updateQuizName) {
        successToast("Assessment updated successfully!");
      }
    },
    update(_, __, { variables }) {
      if (variables?.input.id) {
        const index = myQuizzes.findIndex(plan => plan.id === variables?.input.id);
        if (index > -1) {
          const plan = {
            id: variables?.input.id,
            name: variables.input.name,
            color: myQuizzes[index].color,
            darkColor: myQuizzes[index].darkColor,
            unitIds: myQuizzes[index].unitIds,
            feedbackForm: myQuizzes[index].feedbackForm,
            expiryMinutes: myQuizzes[index].expiryMinutes,
            status: myQuizzes[index].status,
          };
          const newQuizzes = [...myQuizzes];
          newQuizzes[index].name = variables.input.name;
          setSelMyQuiz(plan);
          setMyQuizzes(newQuizzes);
        }
      }
    },
    onError(e) {
      if (showEditModal) setShowEditModal(false);
      logger.info('error', e.message)
    }
  })

  const showAddNewCourse = () => {
    setSelMyQuiz(emptyQuiz)
    setMyQuizMode(MyQuizEnum.QuizBuilder)
    setShowEditModal(true);
    setShowMyQuiz(true)
  }
  // Add new Quiz
  const handleAddNewCourse = useCallback((name: string) => {
    setName(name);
    const color = getRandColor(myQuizzes, colors);
    setColor(color);
    const foundIdx = colors.findIndex(el => el === color);
    const darkColor = darkColors[foundIdx];

    createQuiz({
      variables: {
        input: {
          name,
          color,
          darkColor
        }
      }
    });
  },[createQuiz, myQuizzes])

  // Update Quiz name
  const handleUpdateCourse = useCallback((id: string, name: string, unitId?: string) => {
    setName(name);
    if (id === "") {
      const color = getRandColor(myQuizzes, colors);
      setColor(color);
      const foundIdx = colors.findIndex(el => el === color);
      const darkColor = darkColors[foundIdx];

      createQuiz({
        variables: {
          input: {
            name,
            color,
            darkColor,
            unitId
          }
        }
      });
    } else {
      updateQuizName({
        variables: {
          input: {
            id,
            name
          }
        }
      });
    }
  },[updateQuizName, createQuiz, myQuizzes])

  useEffect(() => {
    if (myQuizData?.myQuizzes && myQuizData.myQuizzes.length > 0) {
      var quizzes = [ ...myQuizData?.myQuizzes || []].map(plan => {
        const color = colors[Math.floor(Math.random() * colors.length)];
        const foundIdx = colors.findIndex(el => el === color)
        const darkColor = darkColors[foundIdx]
        const currentTime = new Date().getTime();
        return {
          id: plan.id,
          name: plan.name,
          feedbackForm: plan.feedbackForm ?? undefined,
          unitIds: plan.unitIds,
          color: plan.color ? plan.color : color,
          darkColor: plan.darkColor ? plan.darkColor : darkColor,
          expiryMinutes: plan.feedbackForm ? (parseInt(plan.feedbackForm.expirationAt ?? '0') - currentTime) / 1000 : 0,
          status: plan.feedbackForm && (parseInt(plan.feedbackForm.expirationAt ?? '0') - currentTime) / 1000 > 0 ? true : false,
        }
      });
      setMyQuizzes(quizzes as ExtendedQuiz[])
      if (quizzes.length > 0) {
        setSelMyQuiz(quizzes[0] as ExtendedQuiz)
      }
    }
  }, [myQuizData?.myQuizzes, setMyQuizzes])

  useEffect(() => {
    if (myQuizData?.myQuizzes && myQuizData.myQuizzes.length === 0) {
      if (user?.firstName && !isDefault) {
        setIsDefault(true);
        handleAddNewCourse(user.firstName);
      }
    }
  }, [myQuizData?.myQuizzes, user?.firstName, isDefault, handleAddNewCourse])

  useEffect(() => {
    const existExpires = myQuizzes.filter(
      (quiz) => {
        const currentTime = new Date().getTime();
        return (
          quiz.feedbackForm && (parseInt(quiz.feedbackForm.expirationAt ?? '0') - currentTime) / 1000 > 0
      )}
    );
    if (existExpires.length > 0) {
      const currentTime = new Date().getTime();
      countDownTimer.current = setInterval(() => {
        const quizzes = myQuizzes.map((quiz) => {
          if (quiz.feedbackForm && (parseInt(quiz.feedbackForm.expirationAt ?? '0') - currentTime) / 1000 > 0) {
            return {
              ...quiz,
              expiryMinutes: (parseInt(quiz.feedbackForm.expirationAt ?? '0') - currentTime) / 1000 - 60,
              status: parseInt(quiz.feedbackForm.expirationAt ?? '0') - 60 >= 0 ? true : false,
            };
          }
          return quiz;
        });
        setMyQuizzes(quizzes);
      }, 60 * 1000); // countdown every 1 min
    }
    return () => {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current);
        countDownTimer.current = null;
      }
    };
  }, [myQuizzes, setMyQuizzes]);

  const completedUnits = useMemo(() => {
    const filteredPrograms = UnitData?.getAllUnitsWithQuiz.filter(program => program.name === 'Your Completed Units')
    return filteredPrograms?.length ? filteredPrograms[0].units as Unit[] : [];
  }, [UnitData?.getAllUnitsWithQuiz]);

  // Select child Quiz
  const handleSelCourse = useCallback((item: ExtendedQuiz) => {
    if (selMyQuiz === item || myQuizMode === MyQuizEnum.MyQuizzes) {
      setSelMyQuiz(undefined)
    } else {
      setSelMyQuiz(item)
    }

  }, [selMyQuiz, myQuizMode])
  // Edit Library
  const handleShowMyQuiz = (item: ExtendedQuiz) => {
    setMyQuizMode(MyQuizEnum.QuizBuilder)
    setSelMyQuiz(item)
    setShowMyQuiz(true)
  }

  const handleEditCourse = (item: ExtendedQuiz) => (e: any) => {
    e.stopPropagation()
    setSelMyQuiz(item)
    setShowEditModal(true)
  }
  const handleSelectPlanMode = useCallback((planMode: MyQuizEnum) => {
    setMyQuizMode(planMode)
    if (planMode !== MyQuizEnum.QuizBuilder) {
      setShowMyQuiz(false);
    } else {
      setShowMyQuiz(true);
    }
  }, [setShowMyQuiz]);

  const handleLessonHovered = useCallback((unitId: string, hovered: boolean) => {
    setHoveredLessonId(unitId);
    setCardHovered(hovered)
    if (hovered) {
      setTimeout(() => {
        document.getElementById("unitsView")!.style.overflow = "hidden"
      }, 500)
    } else {
      document.getElementById("unitsView")!.style.overflow = "auto"
    }
  }, [])
  
  const handleOpenUnit = useCallback((quiz?: ExtendedQuiz) => (unit: Unit) => {
    setSelMyQuiz(quiz);
    if (unit) {
      setSelectUnit(unit);
      history.push(`/assessments?tab=${myQuizMode === MyQuizEnum.MyQuizzes ? '2' : '1'}&uid=${unit?.id}`);
      setShowUnit(true);
    }
    handleLessonHovered('', false)
  }, [history, myQuizMode, handleLessonHovered]);

  // Handle movable lesson
  const handleMovableUnit = useCallback((movable: boolean) => {
    setIsMovable(movable);
  }, [setIsMovable])
  // Move Lesson by right arrow
  const handleMoveUnitToQuiz = useCallback((unit: UnitContentFragment) => {
    try {
      var needUpdate = false, newUnits = [''], courseId = ''
      var currentQuizzes = [...myQuizzes];
      currentQuizzes = currentQuizzes.map((quiz: any, i: number) => {
        if (quiz.id === selMyQuiz?.id) {
          const exist = quiz.unitIds?.find((item: any) => item === unit.id)
          if (exist) {
            return quiz
          } else {
            courseId = quiz.id;
            newUnits = [...quiz.unitIds || [], unit.id]
            needUpdate = true;
            return {
              ...quiz,
              unitIds: newUnits
            }
          }
        } else {
          return quiz
        }
      })
      setMyQuizzes(currentQuizzes);
      if (needUpdate) {
        updateQuiz({
          variables: {
            input: {
              id: courseId,
              unitIds: newUnits
            }
          }
        });
      }
    } catch (e) {
      logger.info(e);
    }
  }, [myQuizzes, selMyQuiz, updateQuiz, setMyQuizzes])

  // Remove Lesson
  const handleRemoveLesson = useCallback((unit: UnitContentFragment) => {
    var newUnits = [''], planId = ''
    const currentQuizzes = myQuizzes.map((plan: any, i: number) => {
      const exist = plan.unitIds?.find((item: any) => item === unit.id)
      if (exist && plan.id === selMyQuiz?.id) {
        planId = plan.id
        newUnits = [...plan.unitIds || []].filter((item: any) => item !== unit.id)
        return {
          ...plan,
          unitIds: newUnits
        }
      }
      return plan
    })
    setMyQuizzes(currentQuizzes);
    updateQuiz({
      variables: {
        input: {
          id: planId,
          unitIds: newUnits
        }
      }
    });
  }, [updateQuiz, setMyQuizzes, selMyQuiz, myQuizzes])

  // handle Drag and Drop on Right Side Quiz
  const handleDnDOnCourseList = useCallback((index: number, units: string[]) => {
    var needUpdate = false, newUnits = [''], planId = ''
    const currentQuizzes = myQuizzes.map((plan: any, i: number) => {
      if (plan.id === selMyQuiz?.id) {
        planId = plan.id;
        newUnits = [...units]
        needUpdate = true;
        return {
          ...plan,
          unitIds: newUnits.reverse()
        }
      } else {
        return plan
      }
    })
    setMyQuizzes(currentQuizzes);
    if (needUpdate) {
      updateQuiz({
        variables: {
          input: {
            id: planId,
            unitIds: newUnits
          }
        }
      });
    }
  }, [updateQuiz, selMyQuiz?.id, setMyQuizzes, myQuizzes]);
  // handle Drag and Drop
  const handleDrop = useCallback(
    (index: number, item: any) => {
      const dropUnit = item.unit as Unit
      if (!selMyQuiz?.id || index === 0) {
        if (index > 0) {
          setDndUnit(dropUnit.id)
          setShowEditModal(true)
        }
        return
      }
      try {
        var needUpdate = false, newUnits = [''], planId = ''
        var currentQuizzes = [...myQuizzes];
        currentQuizzes = currentQuizzes.map((plan: ExtendedQuiz, i: number) => {
          if (plan.id === selMyQuiz?.id) {
            const exist = plan.unitIds?.find((item: any) => item === dropUnit.id)
            if (exist) {
              return plan
            } else {
              planId = plan.id;
              newUnits = [...plan.unitIds || [], dropUnit.id]
              needUpdate = true;
              return {
                ...plan,
                unitIds: newUnits
              }
            }
          } else {
            return plan
          }
        })
        setMyQuizzes(currentQuizzes);
        if (needUpdate) {
          updateQuiz({
            variables: {
              input: {
                id: planId,
                unitIds: newUnits
              }
            }
          });
        }
      } catch (e) {
        logger.info(e);
      }
    }, [myQuizzes, selMyQuiz, updateQuiz, setMyQuizzes]
  );

  //Handle save quiz name 
  const handleSaveChange = ({ name, value, previousValue }) => {
    if (value === '') {
      alert(`You can't change the name with an empty letter. Please try again here.`)
      const item = myQuizzes.find(quiz => quiz.id === name) as ExtendedQuiz
      setSelMyQuiz(item);
      setShowEditModal(true);
      setMyQuizMode(MyQuizEnum.QuizBuilder);
      return
    }
    var needUpdate = false
    const currentQuizzes = myQuizzes.map((quiz: any, i: number) => {
      if (quiz.id === name) {
        needUpdate = true;
        return {
          ...quiz,
          name: value
        }
      } else {
        return quiz
      }
    })
    setMyQuizzes(currentQuizzes)
    if (needUpdate) {
      updateQuizName({
        variables: {
          input: {
            id: name,
            name: value
          }
        }
      });
    }
  }

  const handleQuizPreview = (quiz: ExtendedQuiz) => {
    setSelGenQuiz(quiz)
    setShowQuizPreview(true)
  }

  const handleClosePreview = useCallback(() => {
    setSelGenQuiz(undefined)
    setShowQuizPreview(false)
    setShowQuizView(false)
    refetch()
  }, [refetch])

  const handleGenerateQuiz = (quiz: ExtendedQuiz) => {
    setSelGenQuiz(quiz)
    setShowQuizSessionModal(true)
  }

  const handleStartGenerateQuiz = () => {
    setShowQuizSessionModal(false);
    setShowQuizView(true);
  }

  const handleActiveFormPreview = (feedback: FeedbackFormContent, expiration: any) => {
    const url = createURL(feedback.link ?? "");
    window.navigator.clipboard.writeText(url);
    successToast("Quiz Link Successfully Created and Link copied successfully!");
    setShowQuizGeneration(false)
    refetch()
  }

  const unitsViewStyle = useMemo(() => {
    const classes = [styles.unitsView];
    if (myQuizMode === MyQuizEnum.MyQuizzes) {
      classes.push(styles.leftPanelHidden);
    }
    
    return classes.join(' ');
  }, [myQuizMode])

  const handleGenerateQuizLink = (quiz: ExtendedQuiz) => {
    setSelGenQuiz(quiz)
    setShowQuizGeneration(true)
  }

  const leftViewDiv = (quiz: ExtendedQuiz) => {
    var units: Unit[] = [];
    (quiz?.unitIds || []).forEach(unitId => {
      if (filtered.find(lesson => lesson.id && lesson.id === unitId) as Unit !== undefined) {
        units.push(filtered.find(lesson => lesson.id && lesson.id === unitId) as Unit)
      }
    })
    return <QuizItem 
      quiz={quiz} allUnits={units.reverse()} movedScroll={movedScroll} fetching={quizLoading}
      handleGenerateQuiz={handleGenerateQuiz}
      handleOpenUnit={handleOpenUnit(quiz)}
      handleGenerateQuizLink={handleGenerateQuizLink}
      handleSaveChange={handleSaveChange}
      handleQuizPreview={handleQuizPreview}
      handleShowMyQuiz={handleShowMyQuiz}
      showAddNewCourse={showAddNewCourse}
      handleShowQrCode={handleGenerateQuiz}
      quizRefresh={refetch}
    />
  }

  const handleScroll = useCallback(() => {
    const navMenuElement = document.getElementById("navMenuBar")
    const leftScrollView = document.getElementById("leftScrollView")
    if (leftScrollView) {
      const rect = leftScrollView.getBoundingClientRect();
      const navBarRect = navMenuElement?.getBoundingClientRect();
      if (rect.top === 80) {
        setNavHidden(false)
        setTopPos(HEADER_HEIGHT);
      } else if (navBarRect && navBarRect.height - (87 - rect.top) < 130) {
        setTopPos(rect.top);
      } else {
        setNavHidden(false);
      }
    }
    setMovedScroll((current) => !current);
  }, [])

  const rightMenuStyle = useMemo(() => {
    const classes = [styles.rightView];
    if (showMyQuiz) {
      classes.push(styles.slideShow);
    }
    return classes.join(' ');
  }, [showMyQuiz])

  const leftViewStyle = useMemo(() => {
    const classes = [styles.leftView];
    if (showMyQuiz) {
      classes.push(styles.slideMove);
    }
    return classes.join(' ');
  }, [showMyQuiz])

  const mainViewStyle = useMemo(() => {
    const classes = [styles.libraryView];
    if (!showMyQuiz) {
      classes.push(styles.filterMove);
    }
    return classes.join(' ');
  }, [showMyQuiz])

  const showAddQuizModal = useCallback(() => {
    setShowModal(true);
  }, []);

  var tab_height = useMemo(() => {
    if (topPos < 80) {
      return 0
    }
    return middleWidth ? smallWidth ? 120 : 160 : 203
  }, [middleWidth, smallWidth, topPos]);

  const handleHoverLesson = useCallback((unitId: string, hovered: boolean) => {
    setTopPos(87);    // Make nav tab smaller on Quick info
    setNeedBottomPadding(hovered);
    setTimeout(() => {
      const lessonElement = document.getElementById(unitId);
      if (lessonElement) {        
        lessonElement.scrollIntoView({behavior: "smooth", block: "start", inline: "start"});
      }
      
      setTimeout(() => {
        handleLessonHovered(unitId, hovered)
      }, 100)
    }, 200)
  }, [handleLessonHovered]);

  const onCloseInfo = () => {
    setHoveredLessonId('')
    setNeedBottomPadding(false)
    setCardHovered(false)
    const navMenuElement = document.getElementById("navMenuBar")
    if (navMenuElement) {
      navMenuElement.style.display = 'flex'
      setTopPos(HEADER_HEIGHT);
    }
    setNavHidden(false);
    document.getElementById("unitsView")!.style.overflow = "auto"
  }

  const handleSelectedCenterCard = useCallback((centerCard: boolean) => {
    setCenterCard(centerCard)
  }, [])

  const filteredGrade = useMemo(() => {
    const groups = (isAUuser ? auProgramGradeLevels : usProgramGradeLevels).filter((group, i) => {
      const filteredByAge = (UnitData?.getAllUnitsWithQuiz || []).filter(program => program.name?.includes(group.label))
      if (filteredByAge.length > 0) {
        return true;
      } else {
        return false
      }
    })
    return groups
  }, [UnitData?.getAllUnitsWithQuiz, isAUuser])

  const unitListView = (
    <div className={unitsViewStyle}>
      {<div id={'leftScrollView'} className={myQuizMode === MyQuizEnum.MyQuizzes ? styles.studyModeBackground : ''} style={{height: '100%'}}>
        <TabLayout studyMode={myQuizMode} handleSelectPlanMode={handleSelectPlanMode} topPos={topPos}/>
        {myQuizMode !== MyQuizEnum.MyQuizzes ?
          <div className={styles.mainView} style={{display: showMyQuiz ? 'flex' : 'inline', paddingBottom: needBottomPadding ? 700 : 100}}>
            <div className={mainViewStyle}>
              {completedUnits.length > 0 && <>
                <div className={styles.completedView}>
                  <div className={styles.completedTitleView}>
                    <IconMark /><span>Your Completed Units</span>
                  </div>
                  <Button className={styles.addToQuizButton} size='small' color='orange' label='Add to Assessment'/>
                </div>
                <GridUnitsLayout source={0} target={selMyQuiz?.name} units={completedUnits} studyMode={myQuizMode} 
                  handleDrop={handleDrop} handleOpenUnit={handleOpenUnit(undefined)} showLabel={true} programs={UnitData?.getAllUnitsWithQuiz}
                  handleLeftArrowAction={handleMoveUnitToQuiz} isMovable={isMovable} handleMovableUnit={handleMovableUnit}
                  cardHovered={cardHovered} hoveredLessonId={hoveredLessonId} handleSelectedCenterCard={handleSelectedCenterCard} 
                  handleCardHovered={handleHoverLesson}/>
              </>}
              {filteredGrade.map((grade, i) => {
                const filteredUnitsByGrade = (UnitData?.getAllUnitsWithQuiz || []).find(program => program.name?.includes(grade.label))?.units ?? []
                if (filteredUnitsByGrade.length > 0) {
                  return (
                    <div key={`${grade.value}_${i}`} id="gradeView">
                      <div id={'header' + i} className={`${styles.groupHead} ${isTablet ? styles.headTabletStickyView : styles.headStickyView}`}>
                        <div className={styles.gradeTitle}>
                          {grade.label}
                        </div>
                      </div>
                      <GridUnitsLayout source={0} target={selMyQuiz?.name} units={filteredUnitsByGrade} studyMode={myQuizMode} 
                        handleDrop={handleDrop} handleOpenUnit={handleOpenUnit(undefined)} showLabel={false}
                        handleLeftArrowAction={handleMoveUnitToQuiz} isMovable={isMovable} handleMovableUnit={handleMovableUnit}
                        cardHovered={cardHovered} hoveredLessonId={hoveredLessonId} handleSelectedCenterCard={handleSelectedCenterCard} 
                        handleCardHovered={handleHoverLesson}/>
                    </div>
                  )
                } else {
                  return null
                }
              })}
            </div>
            <div className={rightMenuStyle} id="courseView">
              <div className={styles.rightMenuContainer} style={{height: `calc(100vh - ${tab_height}px - ${HEADER_HEIGHT}px)`}}>
                <div className={styles.planListTitle}>Edit My Assessments:</div>
                {myQuizzes.map((item, i) => {
                  var myQuizUnits: Unit[] = [];
                  item.unitIds?.forEach(unitId => {
                    if (filtered?.find(lesson => lesson.id && lesson.id === unitId) as Unit !== undefined) {
                      myQuizUnits.push(filtered?.find(lesson => lesson.id && lesson.id === unitId) as Unit)
                    }
                  })
                  return (
                    <div key={i}>
                      <div className={styles.planItemContainer}>
                        <div className={styles.planItem} style={{background: `${item.darkColor}`, borderBottomLeftRadius: (item.id === selMyQuiz?.id) ? 0 : '3px', borderBottomRightRadius: (item.id === selMyQuiz?.id) ? 0 : '3px'}} onClick={() => handleSelCourse(item)}>
                          <div className={styles.subItem} style={{background: `#FFFFFF`}}>
                            <div className={styles.subItemTitle} style={{color: `${item.darkColor}`}}>{item.name === "" ? "?" : item.name.substring(0, 1).toLocaleUpperCase()}</div>
                          </div>
                          <div className={styles.planName} style={{color: `white`}}>
                            {item.name === "" ? "No Name" : `${item.name}`}<br/>
                            <span>{item.name === "" ? '' : 'Assessment'}</span>
                          </div>
                          <div className={styles.editPlan} onClick={handleEditCourse(item)}>
                            <IconPencil fill={`white`} />
                          </div>
                          {(myQuizMode === MyQuizEnum.QuizBuilder && !isMobile) && <div className={[styles.courseToggle, selMyQuiz?.id === item.id ? styles.courseToggleClose : styles.courseToggleOpen].join(' ')}/>}
                        </div>
                      </div>
                      <div className={styles.planUnitsView} style={{background: `${item.color}`}}>
                      <Collapse isOpened={(myQuizMode === MyQuizEnum.QuizBuilder && item.id === selMyQuiz?.id)}>
                        <GridUnitsLayout 
                          source={i+1} units={myQuizUnits.reverse()}
                          studyMode={myQuizMode} quiz={item as Quiz} handleDrop={handleDrop}
                          handleRightArrowAction={handleRemoveLesson} handleDnDOnCourseList={handleDnDOnCourseList}
                          isMovable={isMovable} handleMovableUnit={handleMovableUnit} showLabel={true}/>
                      </Collapse></div>
                    </div>
                  )
                })}
                {myQuizMode === MyQuizEnum.QuizBuilder && <div className={styles.itemWrapper}>
                  <div className={styles.separateView}></div>
                  <Button className={styles.addQuizButton} label='+ Create New Assessment' size='small' onClick={showAddQuizModal} />
                </div>}
              </div>
              <div style={{ flexGrow: 1 }}>
              </div>
            </div>
          </div>
        : <div style={{paddingBottom: '100px'}}>{myQuizzes.map(sp => leftViewDiv(sp))}</div>
        }</div>
      }
    </div>
  )

  const coursesForSelectedLesson: Quiz[] = useMemo(() => {
    if (!selectUnit?.id) {
      return [];
    }
    return myQuizzes.filter((plan) => (plan.unitIds || []).includes(selectUnit?.id!)) as Quiz[];
  }, [selectUnit?.id, myQuizzes]);

  const coursesForSelectedLessonIds = useMemo(() => coursesForSelectedLesson.map(unit => unit.id), [coursesForSelectedLesson]);

  const closeModal = () => {
    setShowUnit(false);
    setSelectUnit(null);
    setNeedBottomPadding(false)
    history.replace(`/assessments?tab=${myQuizMode === MyQuizEnum.MyQuizzes ? '2' : '1'}`);
  };

  const handleAddLessonToCourse = useCallback(async (courseId: string) => {
    if (courseId) {
      const selectedCourse = myQuizzes.find(plan => plan.id === courseId);
      if (selectedCourse && selectUnit?.id) {
        await updateQuiz({
          variables: {
            input: {
              id: courseId,
              unitIds: [...(selectedCourse?.unitIds || []), selectUnit?.id]
            }
          }
        });
        setShowModal(false);
      }
    }
  }, [selectUnit?.id, myQuizzes, updateQuiz]);

  const handleConfirmRemove = useCallback(async () => {
    if (deletingQuizId) {
      const deleted = await deleteQuiz({
        variables: {
          input: { id: deletingQuizId }
        }
      });
      if (deleted.data?.deleteQuiz) {
        const courses = await getMyQuizzes();
        if (courses.data?.myQuizzes?.length === 0) {
          setMyQuizzes([])
          setIsDefault(false)
        }
      }
      setRemoveConfirmation(false);
    }
  }, [deleteQuiz, deletingQuizId, getMyQuizzes, setMyQuizzes]);

  const handleCancel = useCallback(() => {
    setRemoveConfirmation(false);
  }, []);

  const handleConfirmRemoveCourse = useCallback((courseId: string) => {
    setShowEditModal(false)
    setDeletingQuizId(courseId);
    setRemoveConfirmation(true);
  }, []);

  const removeQuizName = useMemo(() => {
    if (!!deletingQuizId) {
       const removeStudy = myQuizzes.filter(plan => plan.id === deletingQuizId)
       return removeStudy.length > 0 ? removeStudy[0].name : ''
    }
    return ''
  },[deletingQuizId, myQuizzes])

  const MyPreview = () => {
    const preview = usePreview()
    if (!preview.display) {
      return null
    }
    const {item, style} = preview;
    const { unit } = item as any;
    style.zIndex = '2';
    style.opacity = '0.4';
    style.width = 'calc((72% - 40px)/3 - 20px)';
    style.borderRadius = '3px';
    style.backgroundSize = 'cover';
    style.backgroundImage = `url(${unit.coverImage ? getMediaAbsolutePath(unit.coverImage) : ''})`;
    style.boxShadow = '0px 10px 10px rgba(0, 0, 0, 0.05)';

    return (
      <div style={style}>
        <div className={styles.card}>{}</div>
      </div>
    )
  }

  const HTML5toTouch = {
    backends: [
      {
        id: "html5",
        backend: HTML5Backend,
        transition: MouseTransition
      },
      {
        id: "touch",
        backend: TouchBackend,
        options: { enableTouchEvents: true },
        preview: true,
        transition: TouchTransition,
        skipDispatchOnTransition: true
      }
    ]
  };

  return (
    <>
      <Header isHidden={navHidden}/>
      <div className={styles.wrapper}>
        <DndProvider options={HTML5toTouch}>
          {quizLoading || loading || creating ? <div className={styles.spinner}><Spinner /></div> : <>
          <div id="customunits" className={styles.page}>
            <div id="unitsView" className={leftViewStyle} onScroll={handleScroll}>
              {unitListView}
            </div>

            <div className={styles.gradientView}></div>
          </div>
          <MyPreview />
          </>}
        </DndProvider>
        {cardHovered && <div>
          <div className={styles.blurBackground} onClick={onCloseInfo} style={{bottom: centerCard ? -500 : -300}}/>
        </div>}
      </div>
      {showQuizSessionModal && (
        <InstructionLessonRun
          open={showQuizSessionModal}
          onClose={() => {
            setShowQuizSessionModal(false)
            setSelGenQuiz(undefined)
          }}
          onStartLesson={handleStartGenerateQuiz}
          openMaterial={() => {
            setShowQuizSessionModal(false)
          }}
          title="Pre-Requisite Study Guide"
          mainContent={<div>Please ensure your students have completed the <strong>Study Guide</strong> for each Unit that they are being tested on.</div>}
          downloadLabel="Go Back"
          lastContent="The Questions in the Assessment relate directly to the study guide."
          startLabel="Continue"
        />
      )}
      {showQuizGeneration && 
        <GenerateExitTicketLinkModal 
          open={showQuizGeneration} onClose={() => setShowQuizGeneration(false)} 
          onActiveExitTicket={handleActiveFormPreview} quizType={true} 
          currentQuiz={selGenQuiz as Quiz}
      />}
      {showModal && <AddQuiz open={showModal} quizzes={myQuizzes} creating={creating || updating} onClose={() => setShowModal(false)} onCreateNew={handleAddNewCourse} onAddLessonToCourse={selectUnit?.id ? handleAddLessonToCourse : undefined} coursesForSelectedLessonIds={coursesForSelectedLessonIds} />}
      {removeConfirmation ? <DeleteModal size='small' open={removeConfirmation} onClose={handleCancel} onDelete={handleConfirmRemove} message={`Are you sure you want to delete ${removeQuizName} Assessment?`} loading={deletingCourse || quizLoading} cancelLabel={'NO'} deleteLabel={'DELETE'} /> : null}
      {showEditModal && <EditQuiz open={showEditModal} quiz={selMyQuiz} quizzes={myQuizzes} creating={creating || updating1} onClose={() => setShowEditModal(false)} onUpdateCourse={handleUpdateCourse} onRemoveCourse={handleConfirmRemoveCourse} unitId={dndLesson} />}
      {(showUnit && selectUnit) && <UnitProgressModal open={showUnit} onClose={closeModal} unitId={selectUnit.id} quizUnit={true} />}
      {((showQuizPreview || showQuizView) && selGenQuiz) &&<QuizPreviewModal open={(showQuizPreview || showQuizView)} onClose={handleClosePreview} quiz={selGenQuiz} preview={showQuizPreview? true : false} />}
    </>
  );
}

const QuizBuilder = () => {
  return (
    <CourseContentProvider>
      <QuizBuilderComponent />
    </CourseContentProvider>
  );
};

export default QuizBuilder;